<template>
  <StatPanel class="world-map-stat-panel" v-bind="$props">
    <div class="canvascontainer">
      <canvas ref="canvasElement"></canvas>
    </div>
  </StatPanel>
</template>

<script>
import { ref, watch } from '@vue/composition-api';
import StatPanel from '../StatPanel';
import Chart from 'chart.js';
import * as ChartGeo from 'chartjs-chart-geo';
import deepmerge from 'deepmerge';
import { debounce } from 'lodash-es';
import USATopoJson from './usa.json';
import CanadaTopoJson from './canada.json';
import { useI18n } from '../../../../compositions';
import { crossEnv } from '../../../../setup/configureEnvironment';

export default {
  components: {
    StatPanel,
  },
  props: {
    ...StatPanel.props,
    chartOptions: {
      type: Object,
      required: false,
    },
    data: {
      type: Array,
      required: false,
    },
  },
  setup (props) {
    const i18n = useI18n();
    const provinces = ChartGeo.topojson.feature(CanadaTopoJson, CanadaTopoJson.objects.canadaprov).features;
    const states = ChartGeo.topojson.feature(USATopoJson, USATopoJson.objects.us).features;

    const features = [...provinces, ...states];

    const canvasElement = ref(null);
    let chart;

    function getFormattedData () {
      return features.map(feature => {
        const code = feature.properties.iso_3166_2;
        const data = props.data.find(dataPoint => dataPoint.code === code);
        return {
          feature,
          value: (data) ? data.value : null,
        };
      });
    }

    function initChart () {
      const chartOptions = deepmerge({
        type: 'choropleth',
        data: {
          labels: features.map(state => state.properties.name),
          datasets: [
            {
              label: 'Province or State',
              outline: features,
              data: getFormattedData(),
            },
          ],
        },
        options: {
          showOutline: false,
          showGraticule: false,
          legend: {
            display: false,
          },
          tooltips: {
            callbacks: {
              label (tooltipItem, data) {
                const stateName = data.labels[tooltipItem.index];
                let tooltip = `${stateName}: `;

                const value = parseInt(tooltipItem.value);
                tooltip += (value > 0) ? `${value.toLocaleString(crossEnv.BROWSER_LOCALE)} ${i18n.t('Minutes')}` : i18n.t('No Minutes');
                return tooltip;
              },
            },
          },
          scale: {
            projection: 'geoAlbers',
          },
          geo: {
            colorScale: {
              interpolate: 'OrRd',
              display: false,
              missing: '#ffffff',
            },
          },
        },
      }, props.chartOptions || {});

      chart = new Chart(canvasElement.value, chartOptions);
    }

    function updateChart () {
      chart.data.datasets[0].data = getFormattedData();
      chart.update();
    }

    function watchWindowResizing () {
      if (window) {
        const debouncedListener = debounce(() => {
          initChart();
        }, 300);
        window.addEventListener('resize', () => {
          if (chart) {
            chart.destroy();
            chart = null;
          }
          debouncedListener();
        });
      }
    }

    watchWindowResizing();

    watch(() => props.data, (newVal) => {
      if (newVal) {
        if (chart) updateChart();
        else initChart();
      }
    });

    return {
      canvasElement,
    };
  },
};
</script>

<style lang="postcss" scoped>
.world-map-stat-panel {
  position: relative;
  min-height: 30rem;

  & >>> .loading {
    position: absolute;
    left: calc(50% - 3rem);
    top: calc(50% - 3rem);
  }

  & .canvascontainer {
    position: relative;
    width: 100%;
    height: 50%;
  }
}
</style>
